import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select";

// Connects to data-controller="code-editor"
export default class extends Controller {
  static targets = ["activeCheckbox", "newPermissionsContainer", "permissionsContainer", "newPermissionsInput", "groupSelector", "groupActions", "addButton"];

  static values = {
    valueSelector: String,
    permissionsData: String,
    scope: String,
    translations: String,
  }

  valueElement = null;
  value = {};
  permissionsData = {};
  template = null;
  translations = {};
  tomSelect = null;
  rows = [];
  selectedRows = {};

  connect() {
    this.valueElement = document.querySelector(this.valueSelectorValue)
    this.value = JSON.parse(this.valueElement.value)
    if (!this.value) this.value = {}
    this.permissionsData = JSON.parse(this.permissionsDataValue)
    this.translations = JSON.parse(this.translationsValue)
    Object.keys(this.permissionsData).forEach((key) => {
      this.permissionsData[key] = this.permissionsData[key].sort((a, b) => {
        return this.translations[a]?.localeCompare(this.translations[b])
      })
      if (!this.value[key]) this.value[key] = {}
    });

    this.template = this.permissionsContainerTarget.querySelector('.detail__row')
    Object.keys(this.value).forEach((key) => {
      const inner = this.value[key];

      if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        this.value[key] = Object.fromEntries(
          Object.entries(inner).sort(([a], [b]) => {
            return this.translations[a]?.localeCompare(this.translations[b])
          })
        );
      }

      if (!this.value[key]) this.value[key] = {};
    });

    this.tomSelect = new TomSelect(this.newPermissionsInputTarget, {
      maxItems: 1,
      valueField: "value",
      labelField: "label",
      searchField: "label",
    });

    this.setTomSelectOptions()
    this.generateHTML()
    this.groupSelectorTarget.addEventListener('change', (event) => {
      const keys = Object.keys(this.value[this.scopeValue]).filter(x => x !== '_active_');
      const checked = keys.length !== Object.keys(this.selectedRows).length
      keys.forEach((key) => {
        this.setSelected(key, checked)
      });
      this.generateHTML()
    })
  }

  setSelected(key, checked) {
    if (checked) {
      this.selectedRows[key] = true;
    } else {
      delete this.selectedRows[key];
    }
  }

  setTomSelectOptions() {
    const options = this.permissionsData[this.scopeValue].reduce((acc, permission) => {
      if (!this.value[this.scopeValue][permission])
        acc.push({
          value: permission,
          label: this.translations[permission]
        })
      return acc;
    }, []);
    if (options.length > 1) {
      options.push({
        value: "_all_",
        label: "All"
      })
    } 
    
    if (options.length > 0) {
      this.addButtonTarget.classList.remove('tw:!hidden')
    }else {
      this.addButtonTarget.classList.add('tw:!hidden')
    }
    this.tomSelect.clearOptions();
    this.tomSelect.addOptions(options);
    this.tomSelect.refreshOptions(false);
  }

  refreshValue() {
    this.value = JSON.parse(this.valueElement.value) || {}
    if (!this.value[this.scopeValue]) this.value[this.scopeValue] = {}
  }

  updateActive(event) {
    this.refreshValue()
    this.value[this.scopeValue]["_active_"] = event.target.checked;
    this.valueElement.value = JSON.stringify(this.value);
  }

  updatePermission(event) {
    event.preventDefault();
    this.refreshValue()
    const parentElement = event.target.closest('.detail__row')
    const key = parentElement.dataset.permissionKey;
    this.value[this.scopeValue][key] = event.target.value;
    this.valueElement.value = JSON.stringify(this.value);
  }

  removePermission(event) {
    event.preventDefault();
    this.refreshValue()
    const parentElement = event.target.closest('.detail__row')
    const key = parentElement.dataset.permissionKey;
    delete this.value[this.scopeValue][key]
    this.valueElement.value = JSON.stringify(this.value)
    parentElement.remove()
    this.setTomSelectOptions()
    this.generateHTML()
  }

  generateHTML() {
    this.rows.forEach(row => row.remove());
    this.rows = [];
    this.permissionsData[this.scopeValue].forEach((key) => {
      if (!(this.value?.[this.scopeValue]?.[key])) return
      const value = this.value[this.scopeValue][key]
      const translatedKey = this.translations[key]
      if (!value || !translatedKey) return
      const tpl = this.template
      const row = tpl.cloneNode(true)
      row.classList.remove('tw:hidden')
      row.dataset.permissionKey = key
      row.querySelector(".detail__row__select").textContent = translatedKey
      row.querySelector("select").value = value
      row.querySelector(".detail__row__permission").addEventListener("change", this.updatePermission.bind(this))
      row.querySelector(".detail__row__remove").addEventListener("click", this.removePermission.bind(this))
      const selector = row.querySelector(".detail__row__selector");
      selector.checked = this.selectedRows[key];
      selector.addEventListener('change', (event) => {
        const checked = event.target.checked;
        this.setSelected(key, checked);
        this.generateHeaderHTML();
      })
      this.rows.push(row);
      this.permissionsContainerTarget.appendChild(row);
    });

    this.generateHeaderHTML();
  }

  generateHeaderHTML() {
    const keys = Object.keys(this.value[this.scopeValue]).filter(k => k !== '_active_');
    const selectedLength = Object.keys(this.selectedRows).length
    const allChecked = selectedLength > 0 && keys.length === selectedLength
    if (allChecked) {
      this.groupSelectorTarget.checked = true
      this.groupSelectorTarget.indeterminate = false
    } else if (selectedLength > 0) {
      this.groupSelectorTarget.checked = false
      this.groupSelectorTarget.indeterminate = true
    } else {
      this.groupSelectorTarget.indeterminate = false
      this.groupSelectorTarget.checked = false
    }
    if (keys.length === 0) {
      this.groupSelectorTarget.classList.add('tw:!invisible')
    } else {
      this.groupSelectorTarget.classList.remove('tw:!invisible')
    }
  }

  addPermission() {
    this.newPermissionsContainerTarget.classList.remove("tw:hidden");
  }

  confirmNewPermission(event) {
    this.refreshValue()
    if (this.newPermissionsInputTarget.value === '_all_') {
      this.value[this.scopeValue] = this.permissionsData[this.scopeValue].reduce((acc, current) => {
        if (this.value[this.scopeValue][current]) {
          acc[current] = this.value[this.scopeValue][current]
        } else {
          acc[current] = this.newPermissionsContainerTarget.querySelector('.detail__row__permission').value;
        }
        return acc
      }, {
        "_active_": this.value[this.scopeValue]["_active_"]
      });
    } else {
      this.value[this.scopeValue][this.newPermissionsInputTarget.value] = this.newPermissionsContainerTarget.querySelector('.detail__row__permission').value;
    }
    this.valueElement.value = JSON.stringify(this.value);
    this.newPermissionsContainerTarget.classList.add("tw:hidden");
    this.tomSelect.clear()
    this.newPermissionsContainerTarget.querySelector('.detail__row__permission').value = 'read';
    this.generateHTML()
    this.setTomSelectOptions()
  }

  setSelectedTo(permission) {
    this.refreshValue()
    Object.keys(this.selectedRows).forEach((key) => {
      this.value[this.scopeValue][key] = permission
    })
    this.valueElement.value = JSON.stringify(this.value);
    this.selectedRows = []
    this.generateHTML();
  }

  setSelectedToRead(event) {
    event.preventDefault();
    this.setSelectedTo('read')
  }

  setSelectedToEdit(event) {
    event.preventDefault();
    this.setSelectedTo('edit')
  }

  deleteSelected(event) {
    event.preventDefault()
    this.refreshValue()
    Object.keys(this.selectedRows).forEach((key) => {
      delete this.value[this.scopeValue][key]
    })
    this.valueElement.value = JSON.stringify(this.value);
    this.selectedRows = []
    this.generateHTML();
    this.setTomSelectOptions();
  }
}
